'use client';

import { PricedVariant } from '@medusajs/medusa/dist/types/pricing';
import { Form, Formik, FormikState } from 'formik';
import Image from 'next/image';
import { useState, useContext, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';

import { useTranslation } from '@/app/i18n/client';
import { restrictedBuyLocales } from '@/app/i18n/locale';
import Button from '@/components/Button';
import PartialBorderInput from '@/components/PartialBorderInput';
import ProductGallery from '@/components/ProductGallery';
import CartContext from '@/contexts/CartContext';
import ToastContext from '@/contexts/ToastDialogContext';
import { singleVariantPrice } from '@/utils/functions';
import { newsletterFormValidationSchema } from '@/utils/validation';
import checkmark from 'public/images/checkmark.png';

import ContactInfo from '../common/ContactInfo/ContactInfo';
import Loader from '../Loader';

import { Variants } from './components/Variants/Variants';
import styles from './styles.module.scss';
import { ProductPageProps, Values } from './types';

const ProductPage = ({
  product,
  contact,
  subtitle,
  description,
  lng,
}: ProductPageProps) => {
  const { addItemToCart } = useContext(CartContext);
  const selectedQuantity = useMemo(() => 1, []);
  const firstAvailableVariant = product.variants[0];

  const [selectedVariant, setSelectedVariant] = useState<PricedVariant>(
    firstAvailableVariant,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isUnavailabe] = useState(false);
  const { openDialog } = useContext(ToastContext);
  const { title, variants, thumbnail, images } = product;
  const { cart } = useContext(CartContext);
  const { t } = useTranslation(lng, ['product', 'newsletter']);
  const isBusinessProduct = product.metadata?.business_product === true;
  const showBuyOption = isBusinessProduct
    ? false
    : restrictedBuyLocales.includes(lng)
    ? false
    : true;

  // useEffect(() => {
  //   const unavailableProducts: { title: string; variant?: string }[] = [];
  //   // move this object to the initial array
  //   { title: 'GSH-801 EDMUND' },

  //   const isProductUnavailable = unavailableProducts.some(unavailable => {
  //     if (unavailable.variant) {
  //       return (
  //         product.title === unavailable.title &&
  //         selectedVariant?.title === unavailable.variant
  //       );
  //     } else {
  //       return product.title === unavailable.title;
  //     }
  //   });
  //   setIsUnavailable(isProductUnavailable);
  // }, [product.title, selectedVariant?.title]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const checkVariantAvailability = (id: string) => {
    const productVariant = product.variants.find(variant => variant.id === id);
    let productVariantCartQty = 0;

    if (cart) {
      const productAddedToCart = cart.items.find(el => el.variant_id === id);
      productVariantCartQty = productAddedToCart?.quantity
        ? productAddedToCart?.quantity
        : 0;
    }

    if (productVariant && productVariant.inventory_quantity) {
      return (
        productVariant.inventory_quantity - productVariantCartQty > 0 && true
      );
    }

    return false;
  };

  const handleAddToCart = () => {
    // if (!selectedVariant) {
    //   toast.error(t('toasts.noVariant'));
    //   return;
    // }

    if (selectedVariant.id && checkVariantAvailability(selectedVariant.id)) {
      addItemToCart(selectedVariant.id, selectedQuantity);
    } else {
      toast.error(t('toasts.outOfStock'));
    }
  };

  const selectVariant = (variant: PricedVariant) => {
    if (variant.id && checkVariantAvailability(variant.id)) {
      setSelectedVariant(variant);
    } else {
      toast.error(t('toasts.outOfStock'));
    }
  };

  const initialValues: Values = {
    email: '',
    product: '',
  };

  const onSubmit = async (
    values: Values,
    {
      resetForm,
    }: { resetForm: (nextState?: Partial<FormikState<Values>>) => void },
  ) => {
    const { email } = values;
    setIsLoading(true);

    try {
      const response = await fetch('/api/product-subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          product: product.title,
        }),
      });

      setIsLoading(false);

      if (response.status === 201) {
        openDialog(t('dialog.success', { ns: 'newsletter' }));
        resetForm();
      }

      if (response.status === 500 || response.status === 400) {
        openDialog(t('dialog.duplicate', { ns: 'newsletter' }));
      }
    } catch (err) {
      setIsLoading(false);
      openDialog(t('dialog.error', { ns: 'newsletter' }));
    }
  };

  return (
    <section className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.leftColWrapper}>
          {images?.length && (
            <ProductGallery
              images={images}
              thumbnail={thumbnail}
              title={title}
              selectedVariant={selectedVariant}
              product={product}
            />
          )}
        </div>
        <div className={styles.rightColWrapper}>
          <div className={styles.titleWrapper}>
            <h1 className={styles.title}>{title}</h1>
          </div>
          <div className={styles.subtitleWrapper}>
            <h2 className={styles.subtitle}>{subtitle}</h2>
          </div>
          <div className={styles.descriptionWrapper}>
            <p>{description}</p>
          </div>

          <Variants
            selectVariant={selectVariant}
            selectedVariant={selectedVariant}
            checkVariantAvailability={checkVariantAvailability}
            variants={variants}
            lng={lng}
          />
          {showBuyOption && (
            <>
              <div className={styles.priceWrapper}>
                <p className={styles.price}>
                  {selectedVariant
                    ? singleVariantPrice(selectedVariant)
                    : singleVariantPrice(product.variants[0])}
                </p>
              </div>

              <div className={styles.addToCartSection}>
                {isUnavailabe ? (
                  <Formik
                    initialValues={initialValues}
                    validationSchema={() => newsletterFormValidationSchema(t)}
                    onSubmit={onSubmit}
                  >
                    {({ errors, touched, values, handleChange }) => (
                      <Form className={styles.formContainer}>
                        <div className={styles.formInfoWrapper}>
                          <p>{t('subscription.title')}</p>
                          <p>{t('subscription.description')}</p>
                        </div>
                        <div className={styles.formWrapper}>
                          <PartialBorderInput
                            id="email"
                            type="email"
                            name="email"
                            label={t('subscription.label')}
                            value={values.email}
                            onChange={handleChange}
                          />
                          {errors.email && touched.email && (
                            <p className={styles.error}>{errors.email}</p>
                          )}
                          <Button
                            isNoArrow
                            className={styles.formButton}
                            type="submit"
                          >
                            {!isLoading ? (
                              t('subscription.button')
                            ) : (
                              <Loader isLoaderButton />
                            )}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <>
                    <div className={styles.addToCartBtn}>
                      <Button onClick={handleAddToCart}>
                        {t('cartButton')}
                      </Button>
                      <p>{contact}</p>
                    </div>

                    <div className={styles.flexRowWrapper}>
                      <Image src={checkmark} alt={'checkmark'} quality={100} />
                      <p>{t('installments')}</p>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {isBusinessProduct && (
            <div className={styles.askForPriceWrapper}>
              <p className={styles.askForOffer}>{t('askForOffer')}</p>

              <ContactInfo />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductPage;
